/**
 * Deduct one report credit via Vercel /api/use-credit.
 * In local Vite dev, falls back to a mock when the API is unavailable (no Firestore write).
 */
export interface UseCreditResult {
  success: boolean;
  remainingCredits?: number;
  devMode?: boolean;
  devMessage?: string;
  error?: string;
}

const DEV_MOCK_MESSAGE =
  'Local dev: credit was not deducted. Use `vercel dev` to test the real /api/use-credit endpoint.';

async function callUseCreditApi(idToken: string): Promise<UseCreditResult> {
  let response: Response;
  try {
    response = await fetch('/api/use-credit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    });
  } catch {
    return { success: false, error: 'NETWORK_ERROR' };
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return {
      success: false,
      error: response.ok ? 'INVALID_RESPONSE' : `HTTP_${response.status}`,
    };
  }

  let data: Record<string, unknown> = {};
  try {
    data = await response.json();
  } catch {
    return { success: false, error: 'INVALID_JSON' };
  }

  if (!response.ok) {
    return {
      success: false,
      error: (typeof data.error === 'string' && data.error) || `HTTP_${response.status}`,
    };
  }

  return {
    success: true,
    remainingCredits:
      typeof data.remainingCredits === 'number' ? data.remainingCredits : undefined,
    devMode: Boolean(data.devMode),
    devMessage: typeof data.message === 'string' ? data.message : undefined,
  };
}

function devMockResult(currentCredits?: number): UseCreditResult {
  const base = typeof currentCredits === 'number' ? currentCredits : 1;
  return {
    success: true,
    remainingCredits: Math.max(0, base - 1),
    devMode: true,
    devMessage: DEV_MOCK_MESSAGE,
  };
}

export function stashDevCreditNotice(message: string): void {
  try {
    sessionStorage.setItem('pccheckr_dev_credit_notice', message);
  } catch {
    /* ignore */
  }
}

export function consumeDevCreditNotice(): string | null {
  try {
    const msg = sessionStorage.getItem('pccheckr_dev_credit_notice');
    if (msg) sessionStorage.removeItem('pccheckr_dev_credit_notice');
    return msg;
  } catch {
    return null;
  }
}

/**
 * Deduct one credit. Throws on failure in production.
 * In dev, uses a mock when the Vercel API is not reachable.
 */
export async function deductReportCredit(
  idToken: string,
  options?: { currentCredits?: number }
): Promise<UseCreditResult> {
  const apiResult = await callUseCreditApi(idToken);

  if (apiResult.success) {
    return apiResult;
  }

  if (import.meta.env.PROD) {
    throw new Error(
      apiResult.error === 'NETWORK_ERROR'
        ? 'Could not reach the credit server. Check your connection and try again.'
        : apiResult.error === 'INVALID_RESPONSE' || apiResult.error === 'INVALID_JSON'
          ? 'Credit deduction failed: unexpected server response.'
          : apiResult.error || 'Credit deduction failed'
    );
  }

  // Vite dev: SPA HTML or missing API — mock success for Pro report testing
  console.warn('[use-credit] Dev fallback:', apiResult.error);
  return devMockResult(options?.currentCredits);
}
